import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import {
  KatexText,
  SubmitButton,
  QuizImage,
  QuizSolveDescription,
  QuizHeader,
  QuizResultToggle,
  type QuizResultToggleValue,
} from '../shared'
import type {
  InlineQuizData,
  MultiChoiceQuizData,
  QuizSolveResult,
} from '../../../types/legacyQuiz'
import { formatText } from '../../../utils/quiz-parser'
import { useInlineQuiz } from '../hooks/use-inline-quiz'

interface InlineStudentProps {
  quizId: string | number
  quiz: InlineQuizData
  onSolve?: (
    quizId: string | number,
    answers: string[][],
    vars: Record<string, unknown>,
    elapsedTime: number,
  ) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[][]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[][]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/내용/문제 이미지/보기 텍스트를 흐린다. */
  dimChoiceText?: boolean
}

export function InlineStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: InlineStudentProps) {
  const { t } = useTranslation('quiz')

  const {
    children,
    selectedKeysByChild,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolvedCorrectKeySetByChild,
    perChildCorrectsBySelectedKey,
    totalCorrectCount: _totalCorrectCount,
    totalChildCount: _totalChildCount,
    handleSelectChild,
    handleSubmit,
  } = useInlineQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.inline.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        dimTitleText={dimChoiceText}
        dimProblemText={dimChoiceText}
        dimMedia={dimChoiceText}
        problem={quiz.problem && (
          <div className="text-xl leading-relaxed text-gray-900">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
      />

      {children.map((child, childIdx) => (
        <InlineChildBlockStudent
          key={child.scriptId || childIdx}
          child={child}
          childIdx={childIdx}
          selectedKeys={selectedKeysByChild[childIdx] ?? []}
          isSolved={isSolved}
          isViewSolved={isViewSolved}
          shouldShowCorrectAnswers={shouldShowCorrectAnswers}
          resolvedCorrectKeySet={resolvedCorrectKeySetByChild[childIdx] ?? new Set()}
          correctsBySelectedKey={perChildCorrectsBySelectedKey[childIdx] ?? new Map()}
          onSelect={key => handleSelectChild(childIdx, key)}
          dimChoiceText={dimChoiceText}
          t={t}
        />
      ))}

      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton onClick={handleSubmit} disabled={!canSubmit} label={submitLabel} />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}

function InlineChildBlockStudent({
  child,
  childIdx,
  selectedKeys,
  isSolved,
  isViewSolved,
  shouldShowCorrectAnswers,
  resolvedCorrectKeySet,
  correctsBySelectedKey,
  onSelect,
  dimChoiceText = false,
  t,
}: {
  child: MultiChoiceQuizData
  childIdx: number
  selectedKeys: string[]
  isSolved: boolean
  isViewSolved: boolean
  shouldShowCorrectAnswers: boolean
  resolvedCorrectKeySet: Set<string>
  correctsBySelectedKey: Map<string, boolean>
  onSelect: (key: string) => void
  dimChoiceText?: boolean

  t: any
}) {
  const choices =
    child.random && Array.isArray(child.random) && child.random.length > 0
      ? child.random.map(i => child.choices[i]).filter(Boolean)
      : child.choices

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-lc-gray-500">
        {t('types.inline.childLabel', { n: childIdx + 1 })}
      </div>
      {child.problem && (
        <div className={cn('text-lg leading-relaxed text-gray-900', dimChoiceText && 'opacity-[0.05]')}>
          <KatexText text={child.problem} />
        </div>
      )}
      <div
        className="flex flex-wrap gap-3"
        role="listbox"
        aria-multiselectable="true"
      >
        {choices.map((choice, i) => {
          const isSelected = selectedKeys.includes(choice.key)
          const isInResolvedCorrect = resolvedCorrectKeySet.has(choice.key)
          const isCorrectByCorrects = correctsBySelectedKey.get(choice.key)
          const showAsCorrect =
            isSolved &&
            (shouldShowCorrectAnswers
              ? isInResolvedCorrect
              : isSelected &&
                (isCorrectByCorrects !== undefined ? isCorrectByCorrects : isInResolvedCorrect))
          const isWrongSelected =
            isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect
          const isDisplayedSelected = shouldShowCorrectAnswers
            ? isInResolvedCorrect
            : isSelected
          const isCompleted = isSolved || isViewSolved

          return (
            <button
              key={choice.key}
              role="option"
              aria-selected={isDisplayedSelected}
              onClick={() => !isCompleted && onSelect(choice.key)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-2xl border-2 text-base font-medium transition-all',
                !isSolved && !isSelected && 'text-gray-900 border-lc-gray-400 [box-shadow:0_4px_0_rgba(0,0,0,0.1)]',
                !isSolved && !isSelected && 'hover:text-lc-lightblue-900 hover:border-lc-lightblue-900',
                !isSolved && isSelected && 'text-lc-lightblue-dark bg-lc-lightblue-200 border-lc-lightblue-900',
                showAsCorrect && 'text-lc-mint-900 bg-lc-mint-100 border-lc-mint-900',
                isWrongSelected && 'text-lc-red-900 bg-lc-red-100 border-lc-red-900',
                isSolved && !showAsCorrect && !isWrongSelected && 'border-lc-gray-400 text-gray-900 opacity-60',
                isCompleted && 'pointer-events-none',
              )}
            >
              <span
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
                  !isSelected && !isSolved && 'text-lc-gray-500 bg-lc-gray-100',
                  isSelected && !isSolved && 'text-white bg-lc-lightblue-900',
                  showAsCorrect && 'text-white bg-lc-mint-900',
                  isWrongSelected && 'text-white bg-lc-red-900',
                  isSolved && !showAsCorrect && !isWrongSelected && 'text-lc-gray-500 bg-lc-gray-100',
                )}
              >
                {i + 1}
              </span>
              {choice.image && (
                <div className={cn(dimChoiceText && 'opacity-[0.05]')}>
                  <QuizImage
                    image={choice.image}
                    alt={t('types.multiChoice.optionAlt', { number: i + 1 })}
                    className="my-0 shrink-0"
                  />
                </div>
              )}
              {choice.answer && (
                <span data-quiz-choice-text className={cn(dimChoiceText && 'opacity-[0.05]')}>
                  <KatexText text={choice.answer} />
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
